import DataTable, { Column } from "../../components/DataTable";
import PaginationBar from "../../components/PaginationBar";
import Tag from "../../components/Tags";
import Tabs from "../../components/Tabs";
import useUrlTab from "../../util/hooks/useUrlTab";
import { useEffect, useMemo, useState } from "react";
import ReadMessageModal from "../../components/modal/ReadMessageModal";
import AttachmentViewerModal from "../../components/modal/AttachmentViewModal";
import { AppDispatch, RootState } from "../../store";
import { useBranchCode } from "../../util/hooks/useBranch";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMessages,
  setFilters,
} from "../../features/message-controller/messageSlice";
import { MessageData } from "../../types/messageType";
import { toast } from "sonner";
import messagesApis from "../../api/messagesApis";
import useConfirmToast from "../../util/hooks/useConfirmToast";

const tabOptions = ["All", "Received", "Sent"];

interface MessageItem {
  messageId: number;
  message: string;
  recipient: string;

  senderName: string;

  // sentOn?: string; // backend
  // time?: string;   // backend
  messageType: "SENT" | "RECEIVED" | "OTHER" ;
  attachmentUrl?: string;
  canView: boolean;
}

export default function MessagesList() {
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showReadModal, setShowReadModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<MessageItem | null>(
    null,
  );
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const { confirm, modal } = useConfirmToast();

  const dispatch = useDispatch<AppDispatch>();
  const branchCode = useBranchCode();

  const { messages, pagination, isLoading, filters } = useSelector(
    (state: RootState) => state.messages,
  );

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchMessages({
        branchCode,
        ...filters,
        page: pagination.currentPage,
        size: pagination.pageSize,
      }),
    );
  }, [
    branchCode,
    pagination.currentPage,
    pagination.pageSize,
    filters,
    dispatch,
  ]);

  const handleSearchChange = (accessor: string, value: string) => {
    dispatch(setFilters({ [accessor]: value }));
  };

  const handleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const messageByLabel =
    activeTab === "All"
      ? "Created By"
      : activeTab === "Received"
        ? "Received From"
        : "Recipient";

  useEffect(() => {
    if (activeTab === "All") {
      dispatch(setFilters({ messageType: undefined }));
    } else if (activeTab === "Sent") {
      dispatch(setFilters({ messageType: "SENT" }));
    } else if (activeTab === "Received") {
      dispatch(setFilters({ messageType: "RECEIVED" }));
    }
  }, [activeTab, dispatch]);

  const handleDelete = async () => {
    const isConfrim = await confirm(
      "Are you sure you want to delete this message",
    );
    if (!isConfrim) return;
    try {
      await Promise.all(
        selectedIds.map((id) => messagesApis.deleteMessage(id)),
      );

      toast.success("Message(s) deleted");

      setSelectedIds([]);
      dispatch(
        fetchMessages({
          branchCode: branchCode!,
          page: pagination.currentPage,
          size: pagination.pageSize,
          ...filters,
        }),
      );
    } catch (err) {}
  };

  const columns: Column<MessageItem>[] = [
    {
      label: <input type="checkbox" className="w-3 h-3" />,
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={selectedIds.includes(item.messageId)}
          onChange={() => handleSelect(item.messageId)}
        />
      ),
    },
    {
      label: "SL NO.",
      accessor: "messageId",
      searchable: false,
    },
    {
      label: "Message",
      accessor: "message",
      searchable: true,
      render: (item) => (
        <div className="max-w-52 text-[13px] font-normal text-neutral-400">
          {item.message}
        </div>
      ),
    },
    {
      label: messageByLabel,
      accessor: "recipient",
      render: (item) => {
        if (activeTab === "All") {
          return (
            <div className="max-w-52 text-[13px] font-normal text-neutral-400">
              {item.senderName}
            </div>
          );
        }

        if (activeTab === "Received") {
          return (
            <div className="max-w-52 text-[13px] font-normal text-neutral-400">
              {item.senderName}
            </div>
          );
        }

        // Sent tab
        return (
          <div className="max-w-52 text-[13px] font-normal text-neutral-400">
            {item.recipient}
          </div>
        );
      },
    },

    {
      label: "Date/Time",
      accessor: "sentOn",
      searchable: false,
      render: () => (
        <div className="text-[13px] font-normal text-neutral-400">
          {/* Backend not sending date/time yet */}
          --
        </div>
      ),
    },
    {
      label: "Status",
      accessor: "status",
      render: (item) => {
        const statusColor =
          item.messageType === "SENT" ? "text-warning-700" : "text-success-600";

        return (
          <div className={`${statusColor} text-sm font-semibold capitalize`}>
            {item.messageType}
          </div>
        );
      },
    },
    {
      label: "Attachment",
      accessor: "attachment",
      render: (item) => (
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-primary-500 font-semibold">
            {item.attachmentUrl ? "Added" : "----"}
          </div>
          {item.attachmentUrl && (
            <button
              onClick={() => setAttachmentUrl(item.attachmentUrl!)}
              className="text-[12px] w-fit bg-primary-700 px-3 rounded text-white"
            >
              View
            </button>
          )}
        </div>
      ),
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <Tag
          text="Read"
          color="success"
          padding="wide"
          size="sm"
          onClick={() => {
            setSelectedMessage(item);
            setShowReadModal(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col md:flex-row md:justify-between gap-3 md:items-center">
        <Tabs
          tabs={tabOptions}
          onTabChange={handleTabChange}
          activeTab={activeTab}
        />
        <div className="flex gap-3 items-center justify-end">
          {selectedIds.length > 0 && (
            <button
              onClick={handleDelete}
              className="text-xs font-medium py-[5px] px-[14px] text-white bg-error-500 rounded"
            >
              Delete
            </button>
          )}
          <PaginationBar
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalElements}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
          />
        </div>
      </div>

      <div className="bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={messages}
          isLoading={isLoading}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showReadModal && selectedMessage && (
        <ReadMessageModal
          sender={selectedMessage.senderName}
          message={selectedMessage.message}
          attachment={
            selectedMessage.attachmentUrl || undefined
          }
          onClose={() => {
            setShowReadModal(false);
            setSelectedMessage(null);
          }}
        />
      )}

      {attachmentUrl && (
        <AttachmentViewerModal
          fileUrl={attachmentUrl}
          onClose={() => setAttachmentUrl(null)}
        />
      )}
      {modal}
    </div>
  );
}
